import { isEqual, omit } from 'lodash';
import React, { useEffect, useMemo, useRef } from 'react';
import { usePrevious } from 'react-use';
import { List, RowComponentProps } from 'react-window';
import { useDimensions } from '../hooks/useDimensions';
import { EMPTY_ROW_PROPS, VIRTUALIZED_ROW_HEIGHT } from '../Utils/virtualizationConstants';
import './List.css';
import { ListSelectionType, VirtualizedListProps } from './List.interface';
import { ListRow } from './ListRow';
import styles from './VirtualizedList.module.css';

export const VirtualizedList: React.FC<VirtualizedListProps> = ({
  entries,
  selectionType,
  onSelectionChange,
  selectedEntries,
}) => {
  // The available height is measured explicitly instead of relying on the
  // self-sizing of react-window: a consumer whose ancestors have no definite
  // height (flexbox `min-height: auto`) would otherwise make the list grow to
  // the height of all rows, disabling virtualization.
  const reference = useRef<HTMLDivElement>(null);
  const [listDimensions] = useDimensions<HTMLDivElement>(reference);
  const previousEntries = usePrevious(entries);

  useEffect(() => {
    if (!onSelectionChange) {
      return;
    }

    const a = entries.map(entry => omit(entry, ['avatar', 'actions']));
    const b = previousEntries?.map(entry => omit(entry, ['avatar', 'actions']));

    if (!isEqual(a, b)) {
      onSelectionChange([]);
    }
  }, [entries, previousEntries]);

  const rowClickHandler = useMemo(() => {
    const handleMultiSelection = (index: number) => {
      if (!onSelectionChange) {
        return;
      }
      if (selectedEntries.includes(index)) {
        // Remove
        onSelectionChange(selectedEntries.filter(item => item !== index));
      } else {
        // Add
        onSelectionChange([...selectedEntries, index]);
      }
    };

    const handleSingleSelection = (index: number) => {
      if (!onSelectionChange) {
        return;
      }
      onSelectionChange([index]);
    };

    return selectionType === ListSelectionType.Multi ? handleMultiSelection : handleSingleSelection;
  }, [selectionType, selectedEntries, onSelectionChange]);

  const Row = ({ index, style }: RowComponentProps) => {
    const entry = entries[index];
    const isSelectable =
      selectionType !== ListSelectionType.None &&
      !entry.disabled &&
      !entry.selectionDisabled;

    return (
      <ListRow
        entry={entry}
        style={style}
        isSelected={selectedEntries.includes(index)}
        onClick={() => rowClickHandler(index)}
        isSelectable={isSelectable}
      />
    );
  };

  return (
    <div className={styles.virtualizedList} ref={reference} data-testid="groupListing">
      {listDimensions !== null && (
        <List
          className="list"
          tagName="ul"
          rowComponent={Row}
          rowCount={entries.length}
          rowHeight={VIRTUALIZED_ROW_HEIGHT}
          rowProps={EMPTY_ROW_PROPS}
          style={{
            // The wrapper is a block element, so the width follows on its own.
            height: listDimensions.height,
            // `tagName="ul"` makes this the scroll container, so the browser's
            // default `ul` margin would sit OUTSIDE the measured height: the
            // wrapper would measure `height + 28px`, that value would be fed
            // back as the height, and the list would grow by 28px per measure
            // cycle without bound. Reset here rather than on the shared `.list`
            // class, which the non-virtualized List relies on for its spacing.
            margin: 0,
          }}
        />
      )}
    </div>
  );
};
